/**
 * EntryZoneManager
 *
 * This is a custom component used exclusively for the Zone Details Form.
 * It is designed to emulate most of the same behavior as the OpDataFetchTransfer component,
 * but with customizations specific to the zone details form context.
 *
 * Usage:
 * - Manages a zone's entries.
 * - Allows for entries to be moved to and from zones.
 * - Ensures that no entry is ever left without a zone (entries are never zoneless).
 *
 * Note: This component should only be used within the Zone Details Form and is not intended for general use.
 */
import { useEffect, useRef, useState, Key, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import uniqBy from 'lodash/uniqBy';
import debounce from 'lodash/debounce';
import difference from 'lodash/difference';
import differenceBy from 'lodash/differenceBy';

import { OpTag } from 'new-components/DLS/OpTag/OpTag';
import { OpTransfer } from 'new-components/DLS/OpTransfer/OpTransfer';
import { OpCollapse } from 'new-components/DLS/OpCollapse/OpCollapse';
import { useOpQuery } from 'utils/customHooks/useOpQuery';
import { OpSpace } from 'new-components/DLS/OpSpace/OpSpace';
import { OpForm } from 'new-components/DLS/OpForm/OpForm';
import { OpText } from 'new-components/DLS/OpText/OpText';

import './EntryZoneManager.scss';

interface RecordType {
  key?: number;
  title?: string;
  description?: string;
  disabled?: boolean;
  tooltip?: string | null;
}

interface EntryZoneManagerProps extends Omit<typeof OpTransfer, 'onChange'> {
  orgId: number;
  zoneId?: number;
  disabled?: boolean;
  onChange?: (entryZonesMap: {
    move: Record<string, number>;
    add: number[];
  }) => void;
  value?: { move: Record<string, number>; add: number[] };
}

export const EntryZoneManager = ({
  orgId,
  zoneId,
  disabled,
  onChange: onChangeProp,
  value: valueProp,
}: EntryZoneManagerProps) => {
  const { t } = useTranslation();

  const [availableDataSource, setAvailableDataSource] = useState<RecordType[]>(
    [],
  );
  const [existingDataSource, setExistingDataSource] = useState<RecordType[]>(
    [],
  );
  const [availableTotalItems, setAvailableTotalItems] = useState<number>();
  const [existingTotalItems, setExistingTotalItems] = useState<number>();
  const [availableItemsQuery, setAvailableItemsQuery] = useState<string>();
  const [existingItemsQuery, setExistingItemsQuery] = useState<string>();
  const [targetKeys, setTargetKeys] = useState<Key[]>([]);
  const addedItems = useRef<RecordType[]>([]);
  const removedItems = useRef<RecordType[]>([]);

  const { data: existingItemsHeliumResponse } = useOpQuery({
    apiEndpointName: 'listEntries',
    parameters: [orgId, { ...(zoneId && { preFilter: `zoneId:${zoneId}` }) }],
    enabled: Boolean(zoneId),
  });

  // Get the existing items ids so we can exclude them from the available items
  const existingItemsIds =
    existingItemsHeliumResponse?.json?.data?.map((item) => item.id) || [];

  // We need to exclude the existing items from the available items
  const preFilter =
    `${existingItemsIds.map((id) => `id:(!=${id})`).join(' ')}`.trim();

  const { data: availableItemsHeliumResponse } = useOpQuery({
    apiEndpointName: 'listEntriesPost',
    parameters: [
      orgId,
      {
        limit: 100 + addedItems.current.length, // Makes sure there are always items (if more than 50 in db)
        ...(preFilter && { preFilter }),
        ...(availableItemsQuery && { q: availableItemsQuery }),
      },
    ],
  });

  // Cleanup
  useEffect(
    () => () => {
      addedItems.current = [];
      removedItems.current = [];
    },
    [],
  );

  // Set the existingDataSource
  useEffect(() => {
    const existingItemsData = existingItemsHeliumResponse?.json?.data;

    if (Array.isArray(existingItemsData)) {
      setExistingTotalItems(existingItemsHeliumResponse?.json?.totalCount);

      setExistingDataSource(() =>
        uniqBy(
          existingItemsData.map((item) => ({
            key: item.id,
            title: item.name,
            zoneName: item.zone?.name || '--',
          })),
          'key',
        ),
      );

      // Add group users to targetKeys
      setTargetKeys(
        existingItemsData
          .map((item) => item.id)
          .filter(
            (key) =>
              key &&
              !removedItems.current
                .map(({ key: removedItemKey }) => removedItemKey)
                .includes(key),
          ),
      );
    }
  }, [
    existingItemsHeliumResponse?.json?.data,
    existingItemsHeliumResponse?.json?.totalCount,
  ]);

  // Set the availableDataSource
  useEffect(() => {
    const availableItemsData = availableItemsHeliumResponse?.json?.data;

    if (Array.isArray(availableItemsData)) {
      setAvailableTotalItems(
        availableItemsHeliumResponse?.json?.totalCount || 0,
      );

      setAvailableDataSource(() =>
        uniqBy(
          availableItemsData.map((item) => ({
            key: item.id,
            title: item.name,
            zoneName: item.zone?.name || '--',
          })),
          'key',
        ),
      );
    }
  }, [
    availableItemsHeliumResponse?.json?.data,
    availableItemsHeliumResponse?.json?.totalCount,
  ]);

  const onChange: ComponentProps<typeof OpTransfer>['onChange'] = (
    newTargetKeys,
    direction,
    moveKeys,
  ) => {
    // Update targetKeys
    setTargetKeys(newTargetKeys);

    if (direction === 'right') {
      // Only add tag if wasn't there originally
      const moveKeysNotInRemoveKeys = difference(
        moveKeys,
        removedItems.current.map(({ key }) => key),
      );

      const newAddedItems = [
        ...addedItems.current,
        ...dataSource.filter(({ key }) =>
          moveKeysNotInRemoveKeys.includes(Number(key)),
        ),
      ];
      const newRemovedItems = differenceBy(
        removedItems.current,
        dataSource.filter(({ key }) => key && moveKeys.includes(key)),
        'key',
      );

      const newMoveValue = { ...(valueProp?.move || {}) };
      moveKeys.forEach((key) => {
        delete newMoveValue[Number(key)];
      });

      onChangeProp?.({
        move: newMoveValue,
        add: newAddedItems.map(({ key }) => key!),
      });

      addedItems.current = newAddedItems;

      // Remove old tags
      removedItems.current = newRemovedItems;
    } else {
      // Only add tag if wasn't there originally
      const moveKeysNotInAddKeys = difference(
        moveKeys,
        addedItems.current.map(({ key }) => key),
      );
      const newRemovedItems = [
        ...removedItems.current,
        ...dataSource.filter(({ key }) => moveKeysNotInAddKeys.includes(key)),
      ];
      const newAddedItems = differenceBy(
        addedItems.current,
        dataSource.filter(({ key }) => key && moveKeys.includes(key)),
        'key',
      );

      onChangeProp?.({
        move: { ...(valueProp?.move || {}) },
        add: newAddedItems.map(({ key }) => key!),
      });

      removedItems.current = newRemovedItems;
      // Remove old tags
      addedItems.current = newAddedItems;
    }
  };

  // Stable debounced search handler
  const debouncedSearchRef = useRef(
    debounce((direction, value) => {
      if (direction === 'left') {
        setAvailableItemsQuery(value);
      } else {
        setExistingItemsQuery(value);
      }
    }, 300),
  );

  // For OpTransfer, use debouncedSearchRef.current
  const onSearch = debouncedSearchRef.current;

  // DataSource is the combined responses from available and existing item endpoints
  let dataSource = availableDataSource.concat(existingDataSource);

  // If there is an existing items query, add removed items to the end of the dataSource
  if (existingItemsQuery) {
    dataSource.push(...removedItems.current);
  }

  // Ensure added items persist in the dataSource
  if (availableItemsQuery) {
    dataSource.push(...addedItems.current);
  } else {
    // Add added items to the dataSource if not already present
    const addedItemsNotInDataSource = addedItems.current.filter(
      ({ key }) => !dataSource.some((item) => item.key === key),
    );
    dataSource.push(...addedItemsNotInDataSource);
  }

  // Make sure dataSource items are unique
  dataSource = uniqBy(dataSource, 'key');

  // Sort the dataSource so removed items are at top of available list
  dataSource = dataSource.sort((a, b) => {
    // Sorted so removed items are at top of available list
    const aKeyIndex = a.key
      ? removedItems.current.map(({ key }) => key).indexOf(a.key)
      : -1;
    const bKeyIndex = b.key
      ? removedItems.current.map(({ key }) => key).indexOf(b.key)
      : -1;

    if (aKeyIndex !== -1 && bKeyIndex === -1) {
      // a's key is in removedItems, but b's key is not
      return -1;
    }
    if (aKeyIndex === -1 && bKeyIndex !== -1) {
      // b's key is in removedItems, but a's key is not
      return 1;
    }
    // Both a's key and b's key are in removedItems or both are not in removedItems
    // Preserve the original order
    return 0;
  });

  // Ensure targetKeys includes all added items
  const uniqueTargetKeys = [
    ...new Set(
      addedItems.current.map(({ key }) => key!).concat(targetKeys.map(Number)),
    ),
  ];

  const component = (
    <OpTransfer
      disabled={disabled}
      className="entry-zone-manager"
      dataSource={dataSource}
      showSearch
      selectAllLabels={[
        ({
          selectedCount,
          totalCount,
        }: {
          selectedCount: number;
          totalCount: number;
        }) => {
          const count =
            selectedCount > 0
              ? t('{{count}}/{{totalCount}} items selected', {
                  count: selectedCount,
                  totalCount,
                })
              : t('Select {{count}} item', { count: totalCount });

          const displayedAvailableTotalItems =
            (availableTotalItems || 0) +
            removedItems.current.length -
            addedItems.current.length;

          const total =
            displayedAvailableTotalItems &&
            totalCount !== displayedAvailableTotalItems
              ? `(${t('{{availableTotalItems}} total', {
                  availableTotalItems: displayedAvailableTotalItems,
                })})`
              : '';

          return `${count} ${total}`;
        },
        ({
          selectedCount,
          totalCount,
        }: {
          selectedCount: number;
          totalCount: number;
        }) => {
          const count =
            selectedCount > 0
              ? t('{{count}}/{{totalCount}} items selected', {
                  count: selectedCount,
                  totalCount,
                })
              : t('Select {{count}} item', { count: totalCount });

          const displayedExistingTotalItems =
            (existingTotalItems || 0) +
            addedItems.current.length -
            removedItems.current.length;

          const total =
            displayedExistingTotalItems &&
            totalCount !== displayedExistingTotalItems
              ? `(${t('{{existingTotalItems}} total', {
                  existingTotalItems: displayedExistingTotalItems,
                })})`
              : '';

          return `${count} ${total}`;
        },
      ]}
      styles={{
        section: {
          width: '100%',
          height: 500,
          overflow: 'hidden', // Keeps overall width constrained to drawer width
        },
      }}
      targetKeys={uniqueTargetKeys}
      onChange={onChange}
      onSearch={onSearch}
      filterOption={(inputValue, option) => {
        return option.title!.toLowerCase().includes(inputValue.toLowerCase());
      }}
      render={(record) => {
        const { key: entryId, title, zoneName } = record;

        const isAdded = addedItems.current
          .map(({ key: addedItemKey }) => addedItemKey)
          .includes(Number(entryId));
        const isRemoved = removedItems.current
          .map(({ key: removedItemKey }) => removedItemKey)
          .includes(Number(entryId));

        return {
          label: (
            <div className={clsx('op-data-fetch-transfer__label-wrapper')}>
              <div className="op-data-fetch-transfer__label">
                <OpText ellipsis>{title || '--'}</OpText>
                <OpText
                  className="op-data-fetch-transfer__sub-label"
                  size="small"
                  ellipsis
                >
                  Zone: {zoneName || '--'}
                </OpText>
              </div>

              {isAdded && (
                <OpTag
                  variant="outlined"
                  className="op-data-fetch-transfer__tag"
                >
                  {t('adding')}
                </OpTag>
              )}

              {isRemoved && (
                <OpSpace style={{ justifyContent: 'flex-end' }}>
                  <OpTag
                    variant="outlined"
                    style={{ marginRight: 0 }}
                    className="op-data-fetch-transfer__tag"
                  >
                    {t('moving to')}
                  </OpTag>
                  <OpForm.DataFetchSelect
                    onClick={(e) => e.stopPropagation()}
                    name={['entries', 'move', String(entryId)]}
                    formItemClassName="entry-zone-manager__item-to-move-select"
                    queryOptions={{
                      apiEndpointName: 'listZones',
                      parameters: [orgId],
                    }}
                    placeholder={t('Select zone')}
                    rules={[
                      { required: true, message: t('Must select a zone') },
                    ]}
                    numerifyOptionValues
                    testId="entry-zone-manager-item-to-move-select"
                  />
                </OpSpace>
              )}
            </div>
          ),
          value: title || '--',
        };
      }}
    />
  );

  return (
    <OpCollapse
      items={[
        {
          key: '1',
          label: `${t('Entries')} (${
            (existingTotalItems || 0) +
            addedItems.current.length -
            removedItems.current.length
          })`,
          children: component,
        },
      ]}
    />
  );
};
